import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SVGIcon } from '@superops/super-icons';
import { Label, TextField, RadioButton } from '@superops/ui-design-system';

import { getTranslatedText } from '@nile/components';
import { UnitType } from './NewUnitType';

const recurringModes = {
  UPFRONT: {
    value: 'UPFRONT',
    label: 'client.details.contractinvoice.recurringMode.upfront',
    info: 'client.details.contractinvoice.recurringMode.upfront.info'
  },
  ARREAR: {
    value: 'ARREAR',
    label: 'client.details.contractinvoice.recurringMode.arrear',
    info: 'client.details.contractinvoice.recurringMode.arrear.info'
  },
  ADVANCE: {
    value: 'ADVANCE',
    label: 'client.details.contractinvoice.recurringMode.advanced',
    info: 'client.details.contractinvoice.recurringMode.advanced.info'
  }
};

/* The `ValidRecurringBeforeMap` constant is defining a mapping of time units to their corresponding
values in days. This mapping is used to calculate the maximum number of days for recurring before
based on the selected unit and quantity in the billing frequency. For example, if the billing
frequency unit is 'MONTH' and the quantity is 2, the maximum recurring before days would be 28 * 2 =
56 days. This mapping allows for easy conversion and calculation of days based on different time
units. */
const ValidRecurringBeforeMap = {
  DAY: 1,
  WEEK: 7,
  MONTH: 28,
  YEAR: 365
};

/**
 * The `BillingTypeSelectionfield` function in TypeScript React handles the selection of billing types,
 * including recurring modes and advanced billing options.
 * @param billingtype - The `billingtype` parameter in the `handleRecurringModeChange` function refers
 * to the type of billing mode being selected, such as 'ADVANCE' or 'ARREAR'. This parameter is used to
 * determine how the billing frequency should be updated based on the selected billing mode.
 * @returns The `BillingTypeSelectionfield` component is being returned. It consists of JSX elements
 * that render a selection field for billing types based on the provided props and handle functions.
 * The component includes radio buttons for selecting different billing modes, a text field for
 * entering recurring before days, and additional information displayed based on the selected billing
 * mode.
 */
function BillingTypeSelectionfield({
  frequency,
  isDisabled,
  quantityType,
  handlePropChange,
  handlePropertiesChange
}) {
  const intl = useIntl();
  const { recurringMode = 'upfront', recurringBefore = null } = frequency;

  function handleRecurringModeChange(billingtype) {
    return (newValue) => {
      if (newValue && recurringMode !== billingtype) {
        const updatedFrequency = {
          ...frequency,
          recurringMode: billingtype,
          recurringBefore: null
        };
        if (billingtype === 'ADVANCE') {
          updatedFrequency.recurringBefore = '0';
        }
        if (recurringMode === 'ARREAR') {
          handlePropertiesChange({
            frequency: updatedFrequency,
            change: {
              quantity: null,
              amount: null
            },
            endDate: null
          });
        } else {
          // handlePropChange('frequency', updatedFrequency);
          handlePropertiesChange({
            frequency: updatedFrequency,
            endDate: null
          });
        }
      }
    };
  }

  /* The `maxRecurringBeforeDays` constant is using the `useMemo` hook to calculate the maximum number of
    days for recurring before based on the `frequency` object's `unit` and `quantity` properties. */
  const maxRecurringBeforeDays = useMemo(() => {
    let recurringBeforeMaxVal = ValidRecurringBeforeMap[frequency?.unit] || 28;
    if (frequency?.quantity) {
      recurringBeforeMaxVal =
        ValidRecurringBeforeMap[frequency?.unit] * frequency?.quantity;
    }
    return recurringBeforeMaxVal;
  }, [frequency?.unit, frequency?.quantity]);

  return (
    <div
      className={`fx col g10 mt-20 mb-20 ${
        isDisabled ? 'cursor-not-allowed' : ''
      }`}
    >
      <Label>
        <FormattedMessage id="admin.serviceCatalog.billing.type" />
      </Label>
      <div className="fx actr g20 mt-10">
        <div className="fx actr g10">
          {Object.entries(recurringModes)
            ?.filter((mode) => {
              if (quantityType !== 'UNITS') {
                return mode[0] !== 'ARREAR';
              }
              return true;
            })
            ?.map(([key, recurringmode]) => {
              const { label, value } = recurringmode;
              return (
                <RadioButton
                  disabled={isDisabled}
                  name={value}
                  label={getTranslatedText(intl, label)}
                  checked={recurringMode === value}
                  onChange={handleRecurringModeChange(value)}
                />
              );
            })}
        </div>
        {recurringMode === 'ADVANCE' && (
          <div className="fx actr g5">
            <FormattedMessage id="client.details.contractinvoice.recurringMode.advanced.bellBefore" />
            <span className="fx actr">
              <TextField
                disabled={isDisabled}
                type="number"
                value={recurringBefore?.toString()}
                min={0}
                size="extrasmall"
                onChange={(val) => {
                  handlePropChange('frequency', {
                    ...frequency,
                    recurringBefore: val || '0'
                  });
                }}
                selectValueOnFocus
                max={maxRecurringBeforeDays}
              />
              <UnitType>
                <FormattedMessage id="client.details.contractinvoice.days" />
              </UnitType>
            </span>
          </div>
        )}
      </div>
      {recurringModes[recurringMode] && (
        <div className="fx actr g5">
          <SVGIcon name="About" />
          <FormattedMessage id={recurringModes[recurringMode]?.info} />
        </div>
      )}
    </div>
  );
}

export default BillingTypeSelectionfield;
